import type { PollerRunConnection } from '@app/shared';
import { DataTable, EmptyState, StatusBadge, Text, type Column } from '../ui';
import { absoluteTime, relativeTime, shortId } from '../lib/pollerActivity';

/**
 * The per-connection fetch-diagnostics table (#318), shared by the run-detail drill-down (the
 * connections a tick polled) and the connection-health panel (a connection's recent ticks). One
 * row per (connection, tick): polled / stored / runs-spawned counts, the post-poll backoff
 * multiplier, when it is next due, and any poll error — so "we never fetched this mailbox" and
 * "the fetch errored" are both visible. Reflows to cards below `md` (no sideways scroll).
 */
export interface PollerConnectionsTableProps {
  connections: PollerRunConnection[];
  /** Accessible caption (visually hidden). */
  caption: string;
  /** Empty-state copy when there are no rollups. */
  emptyLabel: string;
  /** Hide the connection column (the run drill-down groups by tick, not connection). */
  hideConnection?: boolean;
}

export function PollerConnectionsTable({
  connections,
  caption,
  emptyLabel,
  hideConnection,
}: PollerConnectionsTableProps) {
  const columns: Column<PollerRunConnection>[] = [
    {
      id: 'polled',
      header: 'Polled',
      align: 'end',
      cell: (c) => num(c.polled),
    },
    {
      id: 'stored',
      header: 'Stored',
      align: 'end',
      cell: (c) => num(c.stored),
    },
    {
      id: 'runs',
      header: 'Runs',
      align: 'end',
      cell: (c) => num(c.runsSpawned),
    },
    {
      id: 'backoff',
      header: 'Backoff',
      align: 'end',
      cell: (c) => (
        <Text as="span" size="sm" tone={c.backoffMultiplier > 1 ? 'secondary' : 'muted'}>
          ×{c.backoffMultiplier}
        </Text>
      ),
    },
    {
      id: 'nextPoll',
      header: 'Next poll',
      align: 'end',
      cell: (c) =>
        c.nextPollAt ? (
          <Text as="span" size="sm" tone="secondary" title={absoluteTime(c.nextPollAt)}>
            {relativeTime(c.nextPollAt)}
          </Text>
        ) : (
          <Text as="span" size="sm" tone="muted">
            —
          </Text>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      align: 'end',
      hideCardLabel: true,
      cell: (c) =>
        c.error ? (
          <StatusBadge size="sm" tone="danger" label="Error" title={c.error} />
        ) : (
          <StatusBadge size="sm" tone="success" label="OK" />
        ),
    },
    {
      id: 'when',
      header: 'Tick',
      align: 'end',
      cell: (c) => (
        <Text as="span" size="sm" tone="muted" title={absoluteTime(c.occurredAt)}>
          {relativeTime(c.occurredAt)}
        </Text>
      ),
    },
  ];

  if (!hideConnection) {
    columns.unshift({
      id: 'connection',
      header: 'Connection',
      cell: (c) => (
        <Text as="span" size="sm" tone="secondary" title={c.connectionId} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {shortId(c.connectionId)}
        </Text>
      ),
    });
  }

  return (
    <DataTable
      columns={columns}
      rows={connections}
      getRowId={(c) => c.id}
      caption={caption}
      rowTestId="poller-connection-row"
      isEmpty={connections.length === 0}
      emptyState={<EmptyState title={emptyLabel} />}
    />
  );
}

function num(n: number) {
  return (
    <Text as="span" size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
      {n}
    </Text>
  );
}
